/**
 * Lens PropTypes
 *
 * PropType definitions for lens types, lens products, and prescription-related components.
 * Used in frame customization and lens selection workflows.
 *
 * @module propTypes/lens
 */

import PropTypes from "prop-types";

/**
 * Lens type shape
 * Represents a category of lenses (e.g., monofocal, progressive, office)
 *
 * @property {string|number} id - Unique lens type identifier
 * @property {string} name - Lens type name
 * @property {string} description - Short description
 * @property {string} detailedDescription - Detailed description (optional)
 * @property {number} priceAdjustment - Price adjustment for this lens type (optional)
 */
export const lensTypeShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  detailedDescription: PropTypes.string,
  priceAdjustment: PropTypes.number,
});

/**
 * Lens product shape
 * Represents a specific lens product with pricing
 *
 * @property {string|number} id - Unique lens identifier
 * @property {string} name - Lens product name
 * @property {string} description - Lens description
 * @property {number} price - Base price (optional)
 * @property {number} price_with_tax - Price including tax
 */
export const lensShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  price: PropTypes.number,
  price_with_tax: PropTypes.number,
});

/**
 * Diopter option shape
 * Represents a prescription strength option for reading glasses
 *
 * @property {string} value - Diopter value (e.g., "+1.00", "+2.50")
 * @property {string} label - Display label
 */
export const diopterOptionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});
